import { Injectable } from '@angular/core';
import { OidcSecurityService } from 'angular-auth-oidc-client';
import { Router } from '@angular/router';
import { Observable, of } from 'rxjs';
import { take, catchError } from 'rxjs/operators';
import { CookieService } from 'ngx-cookie';
import { AuthStatusManager } from './auth-status-manager.service';
import { environment } from '../../environments/environment';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  public isAuthenticated$ = AuthStatusManager.getAuthStatus$();

  // Single global promise to prevent duplicates
  private static globalAuthCheckPromise: Promise<boolean> | null = null;

  public get currentAuthStatus(): boolean {
    return AuthStatusManager.getCurrentAuthStatus();
  }

  constructor(
    private oidcSecurityService: OidcSecurityService,
    private router: Router,
    private cookieService: CookieService
  ) {
    if (!AuthStatusManager.isAlreadyInitialized()) {
      AuthStatusManager.markAsInitialized();
      this.performInitialAuthCheck();
    }
  }

  private performInitialAuthCheck(): void {
    // Check if we already have tokens before making OIDC call
    const hasTokens = this.hasValidToken();
    if (hasTokens) {
      AuthStatusManager.setAuthStatus(true);
      return;
    }

    // Only make OIDC call if no tokens found and no check in progress
    if (!AuthService.globalAuthCheckPromise) {
      AuthService.globalAuthCheckPromise = this.oidcSecurityService
        .checkAuth()
        .pipe(
          take(1),
          catchError(() => of(false))
        )
        .toPromise()
        .then((isAuthenticated) => {
          AuthStatusManager.setAuthStatus(isAuthenticated || false);
          return isAuthenticated || false;
        })
        .finally(() => {
          AuthService.globalAuthCheckPromise = null;
        });
    }
  }

  public isAuthenticated(): Observable<boolean> {
    // Check if we already have tokens - immediate return
    const hasTokens = this.hasValidToken();
    if (hasTokens) {
      AuthStatusManager.setAuthStatus(true);
      return of(true);
    }

    // Return cached status if we have it
    if (this.currentAuthStatus) {
      return of(true);
    }

    // If we have a global promise in progress, wait for it
    if (AuthService.globalAuthCheckPromise) {
      return new Observable((subscriber) => {
        AuthService.globalAuthCheckPromise!.then((result) => {
          subscriber.next(result);
          subscriber.complete();
        }).catch(() => {
          subscriber.next(false);
          subscriber.complete();
        });
      });
    }

    // Create single promise for auth check if none exists
    AuthService.globalAuthCheckPromise = this.oidcSecurityService
      .checkAuth()
      .pipe(
        take(1),
        catchError(() => of(false))
      )
      .toPromise()
      .then((isAuthenticated) => {
        const hasAccessToken = this.hasValidToken();
        const authStatus = isAuthenticated || hasAccessToken;
        AuthStatusManager.setAuthStatus(authStatus);
        return authStatus;
      })
      .catch(() => {
        // If OIDC check fails, fall back to token check
        const hasTokens = this.hasValidToken();
        AuthStatusManager.setAuthStatus(hasTokens);
        return hasTokens;
      })
      .finally(() => {
        AuthService.globalAuthCheckPromise = null;
      });

    // Convert promise to observable
    return new Observable((subscriber) => {
      AuthService.globalAuthCheckPromise!.then((result) => {
        subscriber.next(result);
        subscriber.complete();
      }).catch(() => {
        subscriber.next(false);
        subscriber.complete();
      });
    });
  }

  private hasValidToken(): boolean {
    const accessToken =
      sessionStorage.getItem('access_token') ||
      localStorage.getItem('access_token');
    return !!accessToken;
  }

  public login(): void {
    this.oidcSecurityService.authorize();
  }

  public forceAuthCheck(): Observable<boolean> {
    // Clear all caches and force new check
    AuthService.globalAuthCheckPromise = null;
    AuthStatusManager.clearAuthStatus();
    return this.isAuthenticated();
  }

  public logout(): void {
    // Reset all auth state immediately
    AuthStatusManager.clearAuthStatus();
    AuthService.globalAuthCheckPromise = null;

    // Clear all tokens from storage
    this.clearAllTokens();

    // Clear cookies
    this.cookieService.removeAll();

    // For local development, perform local logout without redirect
    if (environment.isLocal) {
      // Clear OIDC service state without triggering redirect
      this.oidcSecurityService.logoffLocal();
      // Navigate to login
      this.router.navigate(['/login']);
    } else {
      // Use full OIDC logout for non-local environments
      this.oidcSecurityService.logoff();
    }
  }

  private clearAllTokens(): void {
    // Clear from localStorage
    localStorage.removeItem('access_token');
    localStorage.removeItem('id_token');
    localStorage.removeItem('refresh_token');

    // Clear from sessionStorage
    sessionStorage.removeItem('access_token');
    sessionStorage.removeItem('id_token');
    sessionStorage.removeItem('refresh_token');
  }

  public logoutAndRedirect(url: string): void {
    // Reset all auth state immediately
    AuthStatusManager.clearAuthStatus();
    AuthService.globalAuthCheckPromise = null;

    this.clearAllTokens();
    this.cookieService.removeAll();
    window.location.href = url;
  }
}
